import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComponentLibrary = ({ onDragStart, searchTerm, setSearchTerm }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const componentCategories = [
    { id: 'all', name: 'All Components', icon: 'Grid3X3' },
    { id: 'indicators', name: 'Technical Indicators', icon: 'TrendingUp' },
    { id: 'conditions', name: 'Conditions', icon: 'GitBranch' },
    { id: 'actions', name: 'Actions', icon: 'Play' },
    { id: 'risk', name: 'Risk Management', icon: 'Shield' },
    { id: 'data', name: 'Data Sources', icon: 'Database' }
  ];

  const components = [
    // Technical Indicators
    {
      id: 'moving-average',
      name: 'Moving Average',
      category: 'indicators',
      description: 'Simple or exponential moving average calculation',
      icon: 'TrendingUp',
      color: 'bg-blue-500',
      inputs: ['Price Data'],
      outputs: ['MA Value'],
      parameters: ['Period', 'Type (SMA/EMA)']
    },
    {
      id: 'rsi',
      name: 'RSI',
      category: 'indicators',
      description: 'Relative Strength Index momentum oscillator',
      icon: 'Activity',
      color: 'bg-purple-500',
      inputs: ['Price Data'],
      outputs: ['RSI Value'],
      parameters: ['Period (14)']
    },
    {
      id: 'bollinger-bands',
      name: 'Bollinger Bands',
      category: 'indicators',
      description: 'Volatility bands around moving average',
      icon: 'Layers',
      color: 'bg-green-500',
      inputs: ['Price Data'],
      outputs: ['Upper Band', 'Middle Band', 'Lower Band'],
      parameters: ['Period', 'Standard Deviations']
    },
    {
      id: 'macd',
      name: 'MACD',
      category: 'indicators',
      description: 'Moving Average Convergence Divergence',
      icon: 'GitMerge',
      color: 'bg-orange-500',
      inputs: ['Price Data'],
      outputs: ['MACD Line', 'Signal Line', 'Histogram'],
      parameters: ['Fast Period', 'Slow Period', 'Signal Period']
    },
    {
      id: 'volume-profile',
      name: 'Volume Profile',
      category: 'indicators',
      description: 'Volume distribution at price levels',
      icon: 'BarChart3',
      color: 'bg-cyan-500',
      inputs: ['Price Data', 'Volume Data'],
      outputs: ['Volume Profile'],
      parameters: ['Lookback Period']
    },
    
    // Conditions
    {
      id: 'price-crossover',
      name: 'Price Crossover',
      category: 'conditions',
      description: 'Detect when price crosses above/below level',
      icon: 'ArrowUpDown',
      color: 'bg-red-500',
      inputs: ['Price A', 'Price B'],
      outputs: ['Cross Signal'],
      parameters: ['Direction (Above/Below)']
    },
    {
      id: 'threshold-condition',
      name: 'Threshold',
      category: 'conditions',
      description: 'Trigger when value exceeds threshold',
      icon: 'Gauge',
      color: 'bg-yellow-500',
      inputs: ['Input Value'],
      outputs: ['Boolean Signal'],
      parameters: ['Threshold Value', 'Operator']
    },
    {
      id: 'time-condition',
      name: 'Time Filter',
      category: 'conditions',
      description: 'Execute only during specific time periods',
      icon: 'Clock',
      color: 'bg-indigo-500',
      inputs: ['Signal'],
      outputs: ['Filtered Signal'],
      parameters: ['Start Time', 'End Time', 'Days of Week']
    },
    
    // Actions
    {
      id: 'buy-order',
      name: 'Buy Order',
      category: 'actions',
      description: 'Execute buy order with specified parameters',
      icon: 'ArrowUp',
      color: 'bg-emerald-500',
      inputs: ['Buy Signal'],
      outputs: ['Order Status'],
      parameters: ['Order Type', 'Quantity', 'Price']
    },
    {
      id: 'sell-order',
      name: 'Sell Order',
      category: 'actions',
      description: 'Execute sell order with specified parameters',
      icon: 'ArrowDown',
      color: 'bg-rose-500',
      inputs: ['Sell Signal'],
      outputs: ['Order Status'],
      parameters: ['Order Type', 'Quantity', 'Price']
    },
    {
      id: 'alert',
      name: 'Alert',
      category: 'actions',
      description: 'Send notification when condition is met',
      icon: 'Bell',
      color: 'bg-amber-500',
      inputs: ['Trigger Signal'],
      outputs: ['Alert Sent'],
      parameters: ['Message', 'Priority', 'Channels']
    },
    
    // Risk Management
    {
      id: 'stop-loss',
      name: 'Stop Loss',
      category: 'risk',
      description: 'Automatic stop loss order management',
      icon: 'Shield',
      color: 'bg-red-600',
      inputs: ['Position', 'Current Price'],
      outputs: ['Stop Order'],
      parameters: ['Stop Distance', 'Trailing']
    },
    {
      id: 'position-sizing',
      name: 'Position Sizing',
      category: 'risk',
      description: 'Calculate optimal position size based on risk',
      icon: 'Calculator',
      color: 'bg-slate-600',
      inputs: ['Account Balance', 'Risk %'],
      outputs: ['Position Size'],
      parameters: ['Risk Percentage', 'Stop Distance']
    },
    {
      id: 'portfolio-heat',
      name: 'Portfolio Heat',
      category: 'risk',
      description: 'Monitor total portfolio risk exposure',
      icon: 'Thermometer',
      color: 'bg-orange-600',
      inputs: ['All Positions'],
      outputs: ['Risk Level'],
      parameters: ['Max Heat %', 'Correlation Factor']
    },
    
    // Data Sources
    {
      id: 'eua-price',
      name: 'EUA Price Feed',
      category: 'data',
      description: 'Real-time EUA futures price data',
      icon: 'Leaf',
      color: 'bg-green-600',
      inputs: [],
      outputs: ['Price', 'Volume', 'Open Interest'],
      parameters: ['Contract Month', 'Exchange']
    },
    {
      id: 'oil-price',
      name: 'Oil Price Feed',
      category: 'data',
      description: 'Real-time oil futures price data',
      icon: 'Fuel',
      color: 'bg-black',
      inputs: [],
      outputs: ['Price', 'Volume', 'Open Interest'],
      parameters: ['Contract Type', 'Delivery Month']
    },
    {
      id: 'news-sentiment',
      name: 'News Sentiment',
      category: 'data',
      description: 'Real-time news sentiment analysis',
      icon: 'Newspaper',
      color: 'bg-blue-600',
      inputs: [],
      outputs: ['Sentiment Score', 'News Count'],
      parameters: ['Keywords', 'Time Window']
    }
  ];

  const filteredComponents = components?.filter(component => {
    const matchesCategory = selectedCategory === 'all' || component?.category === selectedCategory;
    const matchesSearch = component?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         component?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleDragStart = (e, component) => {
    e?.dataTransfer?.setData('application/json', JSON.stringify(component));
    onDragStart(component);
  };

  return (
    <div className="w-80 bg-card border-r border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground mb-3">Component Library</h2>
        
        {/* Search */}
        <div className="relative mb-4">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-1">
          {componentCategories?.map(category => (
            <Button
              key={category?.id}
              variant={selectedCategory === category?.id ? "default" : "ghost"}
              size="xs"
              onClick={() => setSelectedCategory(category?.id)}
              className="text-xs"
            >
              <Icon name={category?.icon} size={12} className="mr-1" />
              {category?.name}
            </Button>
          ))}
        </div>
      </div>
      {/* Components List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredComponents?.map(component => (
          <div
            key={component?.id}
            draggable
            onDragStart={(e) => handleDragStart(e, component)}
            className="bg-background border border-border rounded-lg p-3 cursor-grab hover:shadow-md transition-shadow duration-200 hover:border-primary/50"
          >
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 ${component?.color} rounded-md flex items-center justify-center flex-shrink-0`}>
                <Icon name={component?.icon} size={16} color="white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground text-sm mb-1">{component?.name}</h3>
                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{component?.description}</p>
                
                {/* Inputs/Outputs */}
                <div className="space-y-1">
                  {component?.inputs?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Icon name="ArrowRight" size={10} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        In: {component?.inputs?.join(', ')}
                      </span>
                    </div>
                  )}
                  {component?.outputs?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Icon name="ArrowLeft" size={10} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        Out: {component?.outputs?.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredComponents?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No components found</p>
            <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or category filter</p>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border">
        <div className="text-xs text-muted-foreground">
          <div className="flex items-center justify-between mb-1">
            <span>Total Components:</span>
            <span className="font-medium">{components?.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Filtered:</span>
            <span className="font-medium">{filteredComponents?.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentLibrary;